import { Navigate, Route, Routes } from "react-router-dom";

import { App_Url_New } from "@/shared/common/static";
import Modal from "@/shared/components/Modal";
import RightSidePanel from "@/shared/components/RightSidePanel";
import { Quotes, useSecureAccess } from "@/shared/permissionBase";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import { AppRoute } from "@/shared/types/routerType";
import { lazy, useMemo } from "react";
import AuthLayout from "../B2C/layout/AuthLayout";
import ScrollToTop from "../shared/components/ScrollToTop";
import AppShell from "../shared/layout/AppShell";

// Admin Routes — extra sub-pages that piggyback on a backend module's permission but aren't
// themselves a backend module (nested detail routes, etc.).
export const LogDetails = lazy(() => import("@/ADMIN/feature/Logs-monitoring/LogDetailsPage"));

export const ServiceTemplate = lazy(
  () => import("@/ADMIN/feature/apiConfig/components/ServiceTemplatePage")
);
// D2C Routes
export const HomePage = lazy(() => import("@/D2C/features/home/HomePage"));
export const LoginPage = lazy(() => import("@/D2C/features/auth/AuthLogin"));
export const ViewPolicyPage = lazy(
  () => import("@/D2C/features/retail-dashboard/components/policies/ViewPolicyPage")
);
export const TravelInsurance = lazy(
  () => import("@/D2C/features/travel-insurance/TravelInsurancePage")
);
export const CarInsurance = lazy(() => import("@/D2C/features/car-insurance/CarInsurancePage"));
export const HealthInsurance = lazy(
  () => import("@/D2C/features/health-insurance/HealthInsurance")
);
export const LifeInsurance = lazy(() => import("@/D2C/features/life-insurance/LifeInsurancePage"));
export const TravelGetQuote = lazy(() => import("@/D2C/features/travel-quote/TravelQuotePage"));
export const CarGetQuote = lazy(() => import("@/D2C/features/car-quote/CarQuotePage"));
export const HealthGetQuote = lazy(() => import("@/D2C/features/health-quote/HealthQuotePage"));
export const LifeGetQuote = lazy(() => import("@/D2C/features/life-quote/LifeQuotePage"));
export const PolicyResult = lazy(() => import("@/D2C/features/policy/PolicyResultPage"));
export const RetailDashboard = lazy(
  () => import("@/D2C/features/retail-dashboard/RetailDashboardPage")
);

// B2C Routes
export const ClientFetchQuote = lazy(() => import("@/B2C/features/clients/ClientFetchQuote"));

// public routes
// D2C routes
const publicRoutes: AppRoute[] = [
  // Home
  { path: App_Url_New.D2CPageUrl.home, element: <HomePage /> },

  {
    // Travel insurance flow
    path: App_Url_New.D2CPageUrl.insurance.travel,
    element: <TravelInsurance />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.travelGetQuote,
    element: <TravelGetQuote />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.travelPolicyResult,
    element: <PolicyResult lob="travel" />,
  },

  // Car insurance flow
  { path: App_Url_New.D2CPageUrl.insurance.car, element: <CarInsurance /> },

  {
    path: App_Url_New.D2CPageUrl.insurance.carGetQuote,
    element: <CarGetQuote />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.carPolicyResult,
    element: <PolicyResult lob="car" />,
  },

  // Health insurance flow
  {
    path: App_Url_New.D2CPageUrl.insurance.health,
    element: <HealthInsurance />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.healthGetQuote,
    element: <HealthGetQuote />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.life,
    element: <LifeInsurance />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.lifeGetQuote,
    element: <LifeGetQuote />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.lifePolicyResult,
    element: <PolicyResult lob="life" />,
  },

  {
    path: App_Url_New.D2CPageUrl.insurance.healthPolicyResult,
    element: <PolicyResult lob="health" />,
  },

  // Retail dashboard and auth
  {
    path: App_Url_New.D2CPageUrl.retailDashboard,
    element: <RetailDashboard />,
  },

  { path: App_Url_New.CommonPageUrl.login, element: <LoginPage /> },
];

// Sub-routes that ride on a backend module's permission but aren't themselves a backend module
// (product/detail variants and legacy aliases). Lovable iframe routes are NOT hardcoded here —
// they're generated straight from each granted module's own backend `path`, so a nav item and
// its route can never drift apart (see NESTED_ROUTING_ELEMENTS in secureNavigation.config.tsx).
const EXTRA_SECURE_ROUTES: AppRoute[] = [
  {
    path: App_Url_New.B2CPageUrl.addQuotes,
    element: <Navigate to={`${App_Url_New.B2CPageUrl.addQuotes}/travel`} replace />,
    module: "quotes",
    action: "view",
  },
  {
    path: `${App_Url_New.B2CPageUrl.addQuotes}/:product`,
    element: <Quotes />,
    module: "quotes",
    action: "view",
  },
  {
    path: "/add-quotes",
    element: <Navigate to={`${App_Url_New.B2CPageUrl.addQuotes}/travel`} replace />,
    module: "quotes",
    action: "view",
  },
  {
    path: "/add-quotes/:product",
    element: <Quotes />,
    module: "quotes",
    action: "view",
  },
  {
    path: App_Url_New.B2CPageUrl.editQuote,
    element: <ClientFetchQuote />,
    module: "quotes",
    action: "view",
  },
  {
    path: "/auth/client/quotes",
    element: <ClientFetchQuote />,
    module: "quotes",
    action: "view",
  },
  {
    path: App_Url_New.AdminPageUrl.apiConfiguration + "/:id" + "/:insurer_id",
    element: <ServiceTemplate />,
    module: "logs",
    action: "list",
  },
  {
    path: `${App_Url_New.AdminPageUrl.logs}/:log_id`,
    element: <LogDetails />,
    module: "logs",
    action: "view",
  },
  {
    path: `${App_Url_New.B2CPageUrl.policies}/:policyId`,
    element: <ViewPolicyPage />,
    module: "policies",
    action: "view",
  },
];

export default function AppRouter() {
  const {
    auth: { user, customerAuth },
  } = usePosterReducers();

  const hasUserAccessToken = Boolean(user?.access_token || user?.user?.access_token);
  const hasCustomerAccessToken = Boolean(customerAuth?.access_token);

  // Everything a user can see is derived once here — the router, sidebar, and header all read
  // from this same hook instead of each independently re-computing it from raw permissions.
  const { routes: secureRoutes, grantedModuleNames, fallbackPath } = useSecureAccess();

  const allowedPrivateRoutes = useMemo(() => {
    const allowedExtraRoutes = EXTRA_SECURE_ROUTES.filter(
      (route) => !route.module || grantedModuleNames.has(route.module)
    );

    return [...secureRoutes, ...allowedExtraRoutes];
  }, [secureRoutes, grantedModuleNames]);

  // No hardcoded per-role landing page — not every role has backend access to any single
  // fixed route, so the fallback is the first module shown in the sidebar (sorted ascending by
  // label), keeping the post-login landing page consistent with what the user sees in the nav.
  const fallbackPrivatePath =
    fallbackPath || allowedPrivateRoutes[0]?.path || App_Url_New.CommonPageUrl.login;

  const renderRoutes = (routes: AppRoute[]) =>
    routes.map(({ path, element }) => {
      const isLoginRoute = path === App_Url_New.CommonPageUrl.login;
      const loginRedirect = hasCustomerAccessToken
        ? App_Url_New.D2CPageUrl.retailDashboard
        : fallbackPrivatePath;

      return (
        <Route
          key={path}
          path={path}
          element={
            isLoginRoute && (hasUserAccessToken || hasCustomerAccessToken) ? (
              <Navigate to={loginRedirect} replace />
            ) : (
              element
            )
          }
        />
      );
    });

  return (
    <>
      <ScrollToTop />
      <Routes>
        {user ? (
          <>
            <Route element={<AuthLayout />}>{renderRoutes(allowedPrivateRoutes)}</Route>
            <Route path="*" element={<Navigate to={fallbackPrivatePath} replace />} />
          </>
        ) : (
          <>
            <Route element={<AppShell />}>{renderRoutes(publicRoutes)}</Route>
            <Route path="*" element={<Navigate to={App_Url_New.D2CPageUrl.home} replace />} />
          </>
        )}
      </Routes>
      <Modal />
      <RightSidePanel />
    </>
  );
}
